/**
 * Business Unit Create Route
 *
 * Create a new business unit using BusinessUnitForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { BusinessUnitForm } from '../../components/business-units';
import { createBusinessUnit, type CreateBusinessUnitInput, ApiError } from '../../lib/apiClient';
import type { CreateBusinessUnitFormData } from '../../components/forms';
import './new.css';

export const Route = createFileRoute('/business-units/new')({
  component: BusinessUnitCreatePage,
});

function BusinessUnitCreatePage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (formData: CreateBusinessUnitFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build create input
      const input: CreateBusinessUnitInput = {
        name: formData.name,
        description: formData.description || undefined,
        unitType: formData.unitType,
        lifecycleStatus: formData.lifecycleStatus || undefined,
        parentBusinessUnitId: formData.parentBusinessUnitId || undefined,
        code: formData.code || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
      };

      const newEntity = await createBusinessUnit(input);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['ListBusinessUnits'],
      });

      // Navigate to the new business unit detail page
      navigate({
        to: `/business-units/${newEntity.entityId}` as string,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: '/business-units', search: { page: 1, pageSize: 25 } });
  };

  return (
    <div className="business-unit-create-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Business Unit</h1>
        <p className="forge-typography--body2 page-header-subtitle">Add a new business unit</p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <BusinessUnitForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
